import { api, authedFetch } from './api.ts';
import type { SheetTab } from './sheet-queries.ts';

/**
 * Where an open workbook fetches what it did not arrive with.
 *
 * A workbook opens with its first tab and the names of the rest; the others,
 * and the pictures any tab places, are fetched as they are needed. Inside the
 * app that is the signed-in API. On a shared link it is the link's own
 * routes, with no session at all — so the editor is told where to ask rather
 * than assuming.
 */
export interface SheetSource {
  tab(spreadsheetId: string, tabId: string): Promise<SheetTab>;
  image(spreadsheetId: string, hash: string): Promise<Blob>;
  /** Store a picture with the workbook. Absent where nothing can be written — a shared link. */
  uploadImage?(spreadsheetId: string, file: Blob): Promise<{ hash: string }>;
}

/** The signed-in API. */
export const signedInSource: SheetSource = {
  tab: (spreadsheetId, tabId) => api.get<SheetTab>(`/api/sheets/${spreadsheetId}/tabs/${tabId}`),
  image: async (spreadsheetId, hash) =>
    (await authedFetch(`/api/sheets/${spreadsheetId}/images/${hash}`)).blob(),
  uploadImage: (spreadsheetId, file) => {
    const form = new FormData();
    form.append('file', file, file instanceof File ? file.name : 'picture');
    return api.upload<{ hash: string }>(`/api/sheets/${spreadsheetId}/images`, form);
  },
};

/**
 * A shared link's routes, under the address its record came from.
 *
 * `record` is that address — the link's own, or a shared folder's for one
 * workbook in it — and the tab and picture routes sit beneath it.
 */
export function linkSource(record: string): SheetSource {
  const get = async (path: string) => {
    const response = await fetch(`${record}/${path}`, { credentials: 'omit' });
    if (!response.ok) throw new Error('That could not be loaded.');
    return response;
  };
  return {
    tab: async (_spreadsheetId, tabId) => (await (await get(`tabs/${tabId}`)).json()) as SheetTab,
    image: async (_spreadsheetId, hash) => (await get(`images/${hash}`)).blob(),
  };
}
